import React, { FC, useEffect, useState } from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import SelectionButton from 'components/common/buttons/SelectionButton';
import { DESCRIPTION_TONE } from 'constants/ai-prompt.constants';

import AIBetaPill from '../../AIBetaPill';

import EventDescriptionAISteps from './EventDescriptionAISteps';

interface EventDescriptionToneModal extends React.ComponentProps<typeof Modal> {
  handleSubmit: (tones: string[], other?: string) => void;
  handleBack: () => void;
  selectedTones?: string[];
  otherInput?: string;
}

/**
 * AI modal prompts for Event description.
 * step 2: Select tone of description
 * @param param0
 * @returns
 */
const EventDescriptionToneModal: FC<React.PropsWithChildren<EventDescriptionToneModal>> = ({
  handleSubmit,
  handleBack,
  selectedTones,
  otherInput,
  open: isOpen,
  onOpenChange: onRequestClose,
  ...props
}) => {
  const [tones, setTones] = useState<string[]>([]);
  const [other, setOther] = useState<string>();

  useEffect(() => {
    setTones(selectedTones || []);
    setOther(otherInput || '');
  }, [selectedTones]);

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onRequestClose?.(false)} {...props}>
      <ModalContent size="md" className="tw-max-w-[556px]">
        <ModalHeader>
          <div className="tw-flex tw-flex-col tw-items-start">
            <AIBetaPill />
            <ModalTitle>Suggest description</ModalTitle>
          </div>
        </ModalHeader>

        <div className="tw-text-sm tw-font-medium tw-leading-5 tw-text-dark-black">
          How should the description sound?
        </div>

        <div className="tw-text-xs tw-font-normal tw-leading-5 tw-text-primary-grey">
          Select any that apply
        </div>

        <div className="tw-pr-15 tw-flex-row">
          {Object.values(DESCRIPTION_TONE).map((val) => {
            const selected = tones.includes(val);
            return (
              <SelectionButton
                key={val}
                item={val}
                onClick={(): void => {
                  const index = tones?.indexOf(val);

                  if (index === -1) {
                    const temp = [...tones];
                    temp.push(val);
                    setTones(temp);
                    tones.push(val);
                  } else {
                    const temp = [...tones];
                    temp.splice(index, 1);

                    setTones(temp);
                  }
                }}
                small
                selected={selected}
              />
            );
          })}

          {tones.includes(DESCRIPTION_TONE.OTHER) && (
            <>
              <div className="tw-mt-4 tw-text-sm tw-font-medium tw-text-dark-black">Other</div>
              <textarea
                value={other}
                rows={10}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                  setOther(event.target.value);
                }}
                className="tw-mt-3 tw-w-full tw-rounded-lg tw-border-solid tw-border-border-grey tw-p-4 focus:tw-outline focus:tw-outline-primary-blue"
              />
            </>
          )}
        </div>

        <ModalFooter className="tw-justify-between sm:tw-justify-between">
          <EventDescriptionAISteps step={2} />
          <div className="tw-flex tw-gap-2">
            <Button
              variant="secondary"
              onClick={(): void => {
                handleBack();
              }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={!(tones && tones.length > 0)}
              onClick={(): void => {
                handleSubmit(tones, other);
              }}
            >
              Next
              <Icon name="arrow-right" size={16} className="tw-ml-2" />
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventDescriptionToneModal;
